import { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./stamprally.css";
import { useClient } from "../../context/ClientContext";
import { useTenant } from "../../context/TenantContext";
import { useLanguage } from "../../context/LanguageContext";
import { useGamification } from "../../context/GamificationContext";
import apiService from "../../services/api.service";
import useNetworkStatus from "./hooks/useNetworkStatus";
import Loader from "../../components/ui/loader";
import StampRallySeries from "./components/StampRallySeries";

export default function StampRally() {
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  const { tenanturlname, campaignId: campaignIdParam } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { tenant } = useTenant();
  const { client, loading: clientLoading } = useClient();
  const { t, languageId, languageCode } = useLanguage();
  const { getCampaignRewardOptions, decryptObject } = useGamification();

  const campaignIdFromState = location.state?.campaignId;
  const campaignIdFromQuery = searchParams.get("campaignId");
  const campaignId = campaignIdParam || campaignIdFromState || campaignIdFromQuery;

  const [rewardOptions, setRewardOptions] = useState(null);
  const [campaignRewards, setCampaignRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // Require auth (matches other gamification routes)
  useEffect(() => {
    if (clientLoading) return;
    if (!client) navigate(`/${tenanturlname}/signin`);
  }, [clientLoading, client, navigate, tenanturlname]);

  useEffect(() => {
    const run = async () => {
      setErrorMsg("");

      if (!campaignId) {
        setLoading(false);
        setErrorMsg("Missing campaign id.");
        return;
      }
      if (!tenant?.tenantid || !client?.clientId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const options = await getCampaignRewardOptions(campaignId);
        setRewardOptions(options || null);

        // Match Osaka API shape: { encryptedData: "..." }
        const res = await apiService.get("/api/gamification/get-rewards", {
          params: {
            tenantid: tenant.tenantid,
            clientid: client.clientId,
            campaignId,
          },
        });

        const encrypted = res?.encryptedData || res?.data?.encryptedData;
        const decrypted = encrypted ? await decryptObject(encrypted) : res;

        setCampaignRewards(Array.isArray(decrypted) ? decrypted : []);
      } catch (e) {
        setErrorMsg(e?.message || "Failed to load Stamp Rally.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [
    campaignId,
    tenant?.tenantid,
    client?.clientId,
    getCampaignRewardOptions,
    decryptObject,
    t,
  ]);

  const sortedSeries = useMemo(() => {
    const series = rewardOptions?.series || [];
    return [...series].sort((a, b) => {
      const aCompleted = campaignRewards.some(
        (r) => (r.challengeId === 0 || r.challengeId == null) && r.seriesId === a.seriesId
      );
      const bCompleted = campaignRewards.some(
        (r) => (r.challengeId === 0 || r.challengeId == null) && r.seriesId === b.seriesId
      );
      return Number(bCompleted) - Number(aCompleted);
    });
  }, [rewardOptions, campaignRewards]);

  // Carousel (scroll-snap)
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef(null);

  const scrollToIndex = (idx) => {
    const el = trackRef.current;
    if (!el) return;
    const next = Math.max(0, Math.min(idx, sortedSeries.length - 1));
    el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" });
  };

  useEffect(() => {
    // reset carousel when data changes
    setActiveIndex(0);
    const el = trackRef.current;
    if (el) el.scrollTo({ left: 0, behavior: "auto" });
  }, [sortedSeries.length]);

  const onTrackScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const idx = Math.round(el.scrollLeft / Math.max(1, el.clientWidth));
      setActiveIndex(Math.max(0, Math.min(idx, sortedSeries.length - 1)));
    });
  };

  if (loading) {
    return <Loader fullscreen label={t("homePage.loading")} size="xl" />;
  }

  return (
    <div className="min-h-screen px-4 py-6">
      {!isOnline && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t("common.networkError") || "Network connection error. Please check your internet."}
        </div>
      )}

      {errorMsg && (
        <div className="mb-4 rounded-lg border border-yellow-300 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
          {errorMsg}
        </div>
      )}

      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-4 text-center text-[22px] font-bold text-theme-secondary">
          Stamp Rally
        </div>

        {sortedSeries.length === 0 ? (
          <div className="rounded-lg border bg-white p-4 text-sm text-gray-700">
            No stamp rally data available.
          </div>
        ) : (
          <div className="relative">
            {/* Track */}
            <div
              ref={trackRef}
              onScroll={onTrackScroll}
              className="ow-hide-scrollbar flex w-full overflow-x-auto scroll-smooth snap-x snap-mandatory"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              {sortedSeries.map((s) => (
                <div
                  key={s.seriesId}
                  className="w-full flex-none snap-start px-1"
                >
                  <StampRallySeries
                    series={s}
                    campaignRewards={campaignRewards}
                    campaignId={campaignId}
                    languageId={languageId}
                    languageCode={languageCode}
                  />
                </div>
              ))}
            </div>

            {/* Arrows */}
            {sortedSeries.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous"
                  onClick={() => scrollToIndex(activeIndex - 1)}
                  disabled={activeIndex === 0}
                  className="hidden md:flex absolute left-[-8px] top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow disabled:opacity-40"
                >
                  <span className="text-2xl leading-none">‹</span>
                </button>
                <button
                  type="button"
                  aria-label="Next"
                  onClick={() => scrollToIndex(activeIndex + 1)}
                  disabled={activeIndex >= sortedSeries.length - 1}
                  className="hidden md:flex absolute right-[-8px] top-1/2 -translate-y-1/2 h-10 w-10 items-center justify-center rounded-full bg-white/80 shadow disabled:opacity-40"
                >
                  <span className="text-2xl leading-none">›</span>
                </button>
              </>
            )}

            {/* Dots */}
            {sortedSeries.length > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                {sortedSeries.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => scrollToIndex(i)}
                    className="h-3 w-3 rounded-full"
                    style={{
                      backgroundColor: i === activeIndex ? "#1434CB" : "#D9D9D9",
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


